import { createFileRoute } from "@tanstack/react-router";
import { I18nProvider } from "@/i18n/I18nProvider";
import { Nav } from "@/components/festival/Nav";
import { Footer } from "@/components/festival/Footer";
import { RichText } from "@/components/admin/RichText";
import { Button } from "@/components/ui/button";
import {
  Leaf,
  TreePine,
  Truck,
  Recycle,
  Lightbulb,
  Moon,
  Sun,
  Droplets,
  Bike,
  Sprout,
  Wind,
  Heart,
} from "lucide-react";
import {
  getSustainabilityPage,
  type PublicSustainabilityPage,
  type SustainabilityCard,
} from "@/lib/sustainability.functions";

const SITE_URL = "https://openluchtfilmfestival.nl";

export const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  Leaf,
  TreePine,
  Truck,
  Recycle,
  Lightbulb,
  Moon,
  Sun,
  Droplets,
  Bike,
  Sprout,
  Wind,
  Heart,
};

function safeUrl(url: string): string | null {
  const u = (url ?? "").trim();
  if (!u) return null;
  if (u.startsWith("/") || u.startsWith("#")) return u;
  if (/^https?:\/\//i.test(u)) return u;
  return null;
}

export const Route = createFileRoute("/duurzaamheid")({
  loader: () => getSustainabilityPage(),
  head: ({ loaderData }) => {
    const title = loaderData?.seoTitle || "Duurzaamheid — Openluchtfilmfestival Apeldoorn";
    const description = loaderData?.seoDescription || "";
    const ogDescription = loaderData?.ogDescription || description;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: ogDescription },
        { property: "og:type", content: "website" },
        { property: "og:url", content: `${SITE_URL}/duurzaamheid` },
        { name: "twitter:card", content: "summary_large_image" },
        ...(loaderData?.heroUrl && /^https:\/\//i.test(loaderData.heroUrl)
          ? [
              { property: "og:image", content: loaderData.heroUrl },
              { name: "twitter:image", content: loaderData.heroUrl },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: `${SITE_URL}/duurzaamheid` }],
    };
  },
  component: SustainabilityPage,
});

function SustainabilityPage() {
  const data = Route.useLoaderData() as PublicSustainabilityPage;
  const primary = safeUrl(data.ctaPrimaryUrl);
  const secondary = safeUrl(data.ctaSecondaryUrl);

  return (
    <I18nProvider lang="nl">
      <div className="min-h-screen bg-background">
        <Nav />
        <main className="pb-24 pt-28 md:pt-36">
          <div className="mx-auto max-w-5xl px-5 md:px-8">
            <p className="font-mono-cinema text-[10px] uppercase tracking-[0.2em] text-gold">
              — Duurzaamheid
            </p>
            <h1 className="mt-3 font-display text-4xl font-medium leading-[1.05] md:text-6xl">
              {data.title}
            </h1>
            {data.intro && (
              <div className="mt-6 max-w-3xl space-y-4 border-l-2 border-gold/40 pl-5 text-lg leading-relaxed text-foreground/80 md:text-xl md:leading-relaxed">
                <RichText html={data.intro} />
              </div>
            )}

            {data.heroUrl && (
              <div className="mt-10 overflow-hidden rounded-2xl border border-border bg-card">
                <img
                  src={data.heroUrl}
                  alt={data.heroAlt ?? "Duurzaamheid bij het Openluchtfilmfestival"}
                  loading="lazy"
                  decoding="async"
                  className="aspect-[16/9] h-full w-full object-cover"
                />
              </div>
            )}

            {data.cards.length > 0 && (
              <section className="mt-14" aria-label="Onze duurzame maatregelen">
                <p className="font-mono-cinema text-[10px] uppercase tracking-[0.2em] text-gold">
                  — Wat we concreet doen
                </p>
                <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {data.cards.map((card: SustainabilityCard, i: number) => {
                    const Icon = ICONS[card.icon] ?? Leaf;
                    return (
                      <li
                        key={card.id}
                        className="group relative flex h-full flex-col rounded-2xl border border-border bg-night-deep/50 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-gold/40 hover:bg-night-deep/80"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gold/15 text-gold transition-colors group-hover:bg-gold/25">
                            <Icon className="h-5 w-5" />
                          </div>
                          <span className="font-mono-cinema text-[11px] tracking-[0.2em] text-foreground/25">
                            {String(i + 1).padStart(2, "0")}
                          </span>
                        </div>
                        <h2 className="mt-4 font-display text-xl">{card.title}</h2>
                        <p className="mt-2 text-sm leading-relaxed text-foreground/75">
                          {card.text}
                        </p>
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}

            {data.body && (
              <section className="mt-16 overflow-hidden rounded-2xl border border-gold/25 bg-gradient-to-br from-night-deep/80 to-night-deep/30 p-7 md:p-10">
                <RichText
                  html={data.body}
                  className="max-w-3xl text-base leading-relaxed text-foreground/80 md:text-lg"
                />
                {(primary || secondary) && (
                  <div className="mt-8 flex flex-wrap gap-3">
                    {primary && data.ctaPrimaryLabel && (
                      <Button asChild variant="hero" size="lg">
                        <a href={primary}>{data.ctaPrimaryLabel}</a>
                      </Button>
                    )}
                    {secondary && data.ctaSecondaryLabel && (
                      <Button asChild variant="ghostLine" size="lg">
                        <a href={secondary}>{data.ctaSecondaryLabel}</a>
                      </Button>
                    )}
                  </div>
                )}
              </section>
            )}

            {!data.body && (primary || secondary) && (
              <div className="mt-14 flex flex-wrap gap-3">
                {primary && data.ctaPrimaryLabel && (
                  <Button asChild variant="hero" size="lg">
                    <a href={primary}>{data.ctaPrimaryLabel}</a>
                  </Button>
                )}
                {secondary && data.ctaSecondaryLabel && (
                  <Button asChild variant="ghostLine" size="lg">
                    <a href={secondary}>{data.ctaSecondaryLabel}</a>
                  </Button>
                )}
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </I18nProvider>
  );
}